import { AppPageLayout } from "@/components/common/AppPageLayout";
import { Skeleton } from "@patient-watch/ui/skeleton";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { FormPublicCover } from "@/features/pages/components/FormPublicCover";
import {
    formPageRouteShellClass,
    formPageShellFillClass,
    publicFormRoutePaddingClass,
} from "@/features/pages/components/formPageLayout";
import { useFormNavigationStore } from "@/features/pages/hooks/useFormNavigationStore";
import {
    formDisplayOptions,
    formInstructionsPlacement,
    formTitleAlign,
    trimmedField,
} from "@/features/pages/utils/formChrome";
import { useValidationByIdQuery } from "@/features/validations/api/selectValidationById";
import { formNameLabel } from "@/features/navigation/services/entityDisplayLabel";
import { startValidationFormSession } from "@/features/validations/utils/validationFormSession";
import { useNav } from "@/utils/nav";
import type { FormViewT } from "@patient-watch/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type PageWithInputs = { inputs?: unknown[] };

const ValidationFormCover = () => {
    const { validationId, formId } = Route.useParams();
    const navigate = useNavigate();
    const { locale } = useAppLocale();
    const copy = useFormPageCopy();
    const { data: validation, isLoading: isValidationLoading } = useValidationByIdQuery(validationId);
    const { data: form, isLoading: isFormLoading } = useSelectFormById(formId, locale);
    const resetEntryValues = useEntryValuesStore((state) => state.resetEntryValues);
    const [isStarting, setIsStarting] = useState(false);

    useNav([
        { label: "Validate", route: { to: "/validate" } },
        {
            label: formNameLabel(form, validation?.entity_name || "Review"),
            route: { to: "/validate/$validationId/forms/$formId", params: { validationId, formId } },
        },
    ]);

    useEffect(() => {
        if (isValidationLoading || !validation) return;
        if (validation.entity_type !== "form") {
            void navigate({ to: "/validate/$validationId", params: { validationId }, replace: true });
            return;
        }
        if (validation.entity_id !== formId) {
            void navigate({
                to: "/validate/$validationId/forms/$formId",
                params: { validationId, formId: validation.entity_id },
                replace: true,
            });
        }
    }, [formId, isValidationLoading, navigate, validation, validationId]);

    const handleStartForm = () => {
        if (isStarting) return;
        setIsStarting(true);
        resetEntryValues();
        const session = startValidationFormSession(validationId, formId);
        useFormNavigationStore.getState().resetHistory(session.entryId);
        void navigate({
            to: "/validate/$validationId/forms/$formId/page/$pageIndex",
            params: { validationId, formId, pageIndex: "1" },
        });
    };

    if (isValidationLoading || isFormLoading) {
        return (
            <AppPageLayout
                width="md"
                padding="sm"
                className={`${publicFormRoutePaddingClass} ${formPageRouteShellClass}`}
            >
                <div className={`${formPageShellFillClass} gap-4`}>
                    <Skeleton className="w-3/4 h-8" />
                    <Skeleton className="w-full h-4" />
                    <Skeleton className="w-2/3 h-4" />
                </div>
            </AppPageLayout>
        );
    }

    if (!form || !validation || validation.entity_type !== "form" || validation.entity_id !== formId) {
        return null;
    }

    const f = form as Partial<FormViewT>;
    const numberOfQuestions = Array.isArray(f.pages)
        ? f.pages.reduce(
              (acc: number, page: PageWithInputs) => (Array.isArray(page?.inputs) ? acc + page.inputs.length : acc),
              0
          )
        : 0;

    return (
        <AppPageLayout width="md" padding="sm" className={`${publicFormRoutePaddingClass} ${formPageRouteShellClass}`}>
            <FormPublicCover
                name={trimmedField(f.form_name)}
                caption={trimmedField(f.form_caption)}
                kicker={trimmedField(f.form_kicker)}
                copyright={trimmedField(f.copyright_info)}
                instructions={formInstructionsPlacement(f) === "cover" ? trimmedField(f.form_instructions) : null}
                timeToComplete={f.time_to_complete || null}
                numberOfQuestions={numberOfQuestions > 0 ? numberOfQuestions : null}
                titleAlign={formTitleAlign(f)}
                showMetrics={formDisplayOptions(f).showCoverMetrics}
                startLabel={isStarting ? copy.startingForm : copy.startForm}
                isStarting={isStarting}
                onStart={handleStartForm}
            />
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/_app/validate/$validationId/forms/$formId/")({
    component: ValidationFormCover,
});
